import { useState, useMemo } from "react";
import { useRoute } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SUB_GROUP_ICONS } from "@/lib/category-icons";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, MessageSquare, Download, Phone, Mail, Building2, MapPin, HardHat, BrickWall, Construction } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { generateQuotePDF } from "@/lib/pdf-generator";
import { readPdfTemplateSnapshot } from "@workspace/api-zod/pdf-template";
import { quoteHeadingLabel } from "@workspace/api-zod/quote-headings";
import { quoteCategoryLabel } from "@/lib/quote-heading-label";
import { withQuotedCostSplit } from "@workspace/api-zod/quote-cost-split";
import { BrandingDisplay } from "@/components/branding-display";
import { ITEM_SUB_GROUP_LABELS, type ItemSubGroupId } from "@workspace/api-zod/work-type";
import { getEffectiveStage, getEffectiveSubGroup, QUOTE_PLACEMENT_STAGE_ORDER } from "@/lib/section-placement";

interface PublicQuoteResponse {
  quote: any;
  lineItems: any[];
  builder: {
    name: string | null;
    email: string | null;
    companyName: string | null;
    phone: string | null;
    logoUrl: string | null;
    address?: string | null;
    website?: string | null;
    vatNumber?: string | null;
    market?: string | null;
    showCompanyName?: boolean | null;
    logoSize?: "small" | "medium" | "large" | null;
  };
  share: {
    token: string;
    status: string;
    respondedAt: string | null;
    responseMessage: string | null;
    message: string | null;
  };
}

function fmtMoney(n: string | number | null | undefined, currency = "£") {
  if (n === null || n === undefined || n === "") return `${currency}0.00`;
  const v = typeof n === "string" ? parseFloat(n) : n;
  return `${currency}${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PublicQuotePage() {
  const [, params] = useRoute("/q/:token");
  const token = params?.token || "";
  const { toast } = useToast();

  const { data, isLoading, error, refetch } = useQuery<PublicQuoteResponse>({
    queryKey: ["public-quote", token],
    queryFn: async () => {
      const res = await fetch(`/api/public/quote/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to load quote");
      }
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  const [acceptOpen, setAcceptOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const respondMutation = useMutation({
    mutationFn: async (action: "accept" | "request-changes") => {
      const res = await fetch(`/api/public/quote/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, message: responseMessage }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || "Failed to send response");
      return body;
    },
    onSuccess: (_, action) => {
      toast({
        title: action === "accept" ? "Quote accepted" : "Change request sent",
        description: action === "accept"
          ? "Thanks — your builder has been notified."
          : "Thanks — your builder will be in touch.",
      });
      setAcceptOpen(false);
      setChangesOpen(false);
      setResponseMessage("");
      refetch();
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to send response", variant: "destructive" });
    },
  });

  const sortedItems = useMemo(() => {
    if (!data?.lineItems) return [];
    const customStages = Array.from(new Set(
      data.lineItems
        .map(item => getEffectiveStage(item))
        .filter(stage => !QUOTE_PLACEMENT_STAGE_ORDER.includes(stage as any)),
    ));
    const stageOrder = [...QUOTE_PLACEMENT_STAGE_ORDER, ...customStages];
    return data.lineItems.map(withQuotedCostSplit).sort((a, b) => {
      const stageDelta = stageOrder.indexOf(getEffectiveStage(a) as any) - stageOrder.indexOf(getEffectiveStage(b) as any);
      return stageDelta || (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
  }, [data?.lineItems]);

  const coreItems = useMemo(() => sortedItems.filter(item => !item.isOptional), [sortedItems]);
  const optionalItems = useMemo(() => sortedItems.filter(item => !!item.isOptional), [sortedItems]);

  /** Summary mode: one row per category with sub-group breakdown. */
  const categorySummaryRows = useMemo(() => {
    if (!data?.quote?.summaryMode) return null;
    const catTotals = new Map<string, number>();
    const catSubGroupMaps = new Map<string, Map<string, number>>();
    const subGroupIdOfLabel = new Map<string, string>();
    for (const item of coreItems) {
      const sg = getEffectiveSubGroup(item);
      // Customer summaries must respect quote-local placement overrides. In
      // particular, a moved item stays in its persisted display stage rather
      // than being pulled back into the canonical section for its subgroup.
      const section = getEffectiveStage(item);
      catTotals.set(section, (catTotals.get(section) ?? 0) + Number(item.subtotal ?? 0));
      if (sg) {
        const label = ITEM_SUB_GROUP_LABELS[sg as ItemSubGroupId] ?? sg;
        subGroupIdOfLabel.set(label, sg);
        if (!catSubGroupMaps.has(section)) catSubGroupMaps.set(section, new Map());
        const sgMap = catSubGroupMaps.get(section)!;
        sgMap.set(label, (sgMap.get(label) ?? 0) + Number(item.subtotal ?? 0));
      }
    }
    const sectionOrder = [
      ...QUOTE_PLACEMENT_STAGE_ORDER,
      ...Array.from(catTotals.keys()).filter(sec => !QUOTE_PLACEMENT_STAGE_ORDER.includes(sec as any)),
    ];
    return sectionOrder.filter(sec => catTotals.has(sec)).map(sec => ({
      category: sec,
      total: catTotals.get(sec) ?? 0,
      subGroups: Array.from(catSubGroupMaps.get(sec)?.entries() ?? []).map(([label, total]) => ({ label, total, sgId: subGroupIdOfLabel.get(label) })),
    }));
  }, [coreItems, data?.quote?.summaryMode]);

  const handleDownload = async () => {
    if (!data) return;
    const q = data.quote;
    await generateQuotePDF({
      pdfTemplate: readPdfTemplateSnapshot(q.pdfTemplate),
      headingNames: q.headingNames,
      quoteNumber: q.quoteNumber,
      status: q.status,
      projectAddress: q.projectAddress,
      projectType: q.projectType,
      description: q.description,
      subtotal: q.subtotal,
      overheadAmount: q.overheadAmount,
      profitAmount: q.profitAmount,
      contingencyAmount: q.contingencyAmount,
      totalPrice: q.totalPrice,
      vatRate: q.vatRate,
      vatAmount: q.vatAmount,
      grandTotal: q.grandTotal,
      includeVat: q.includeVat,
      showCostBreakdown: q.showCostBreakdown,
      showMarkupBreakdown: (q as any).showMarkupBreakdown !== false,
      showItemDescriptions: q.showItemDescriptions !== false,
      showQuantityUnits: q.showQuantityUnits !== false,
      showLineItems: q.showLineItems !== false,
      validUntil: q.validUntil,
      assumptions: q.assumptions,
      exclusions: q.exclusions,
      termsConditions: q.termsConditions,
      paymentTerms: q.paymentTerms,
      projectTimeline: q.projectTimeline,
      commencementNote: q.commencementNote,
      createdAt: q.createdAt,
      confidenceLevel: q.confidenceLevel,
      summaryMode: (q as any).summaryMode || false,
      lineItems: sortedItems.map(item => ({
        section: item.section || "MAIN WORKS",
        category: item.category,
        name: item.name,
        description: item.description,
        customerDescription: item.customerDescription,
        inclusions: item.inclusions || [],
        exclusions: item.exclusions || [],
        quantity: item.quantity,
        unit: item.unit,
        unitCost: item.unitCost,
        labourCost: item.labourCost,
        materialCost: item.materialCost,
        plantCost: item.plantCost,
        subtotal: item.subtotal,
        isOptional: item.isOptional || false,
         sortOrder: item.sortOrder ?? null,
         displayStage: item.displayStage ?? null,
         displaySubGroup: item.displaySubGroup ?? null,
      })),
      client: q.client ? {
        name: q.client.name,
        email: q.client.email || undefined,
        phone: q.client.phone || undefined,
        address: q.client.address || undefined,
        postcode: q.client.postcode || undefined,
      } : null,
    } as any, data.builder ? {
      name: data.builder.companyName || data.builder.name || "Your Builder",
      address: data.builder.address || undefined,
      phone: data.builder.phone || undefined,
      email: data.builder.email || undefined,
      website: data.builder.website || undefined,
      vatNumber: data.builder.vatNumber || undefined,
      logoUrl: data.builder.logoUrl || undefined,
      showCompanyName: data.builder.showCompanyName ?? true,
      logoSize: data.builder.logoSize || "medium",
      market: data.builder.market || undefined,
    } as any : undefined);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f8f9]">
        <Loader2 className="h-8 w-8 animate-spin text-[#374349]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f6f8f9] p-6">
        <Card className="max-w-md w-full bg-[#f0f4f5] border-[#dbe4e6]">
          <CardContent className="p-8 text-center space-y-3">
            <h1 className="text-xl font-semibold text-[#374349]">Quote unavailable</h1>
            <p className="text-sm text-muted-foreground">
              {(error as any)?.message || "This quote link is invalid or has expired."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { quote, builder, share } = data;
  const responded = !!share.respondedAt;
  const isAccepted = share.status === "accepted";
  const isChangesRequested = share.status === "changes_requested";
  const showCompanyName = builder.showCompanyName ?? true;

  return (
    <div className="min-h-screen bg-[#f6f8f9]">
      {/* Header */}
      <header className="bg-[#374349] text-white">
        <div className="max-w-5xl mx-auto px-6 py-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[#5aabbf] text-xs font-semibold tracking-wider uppercase mb-1">Your Quote</div>
            <h1 className="text-2xl sm:text-3xl font-bold">Quote {quote.quoteNumber}</h1>
            {quote.projectAddress && (
              <div className="text-white/80 text-sm mt-1 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {quote.projectAddress}
              </div>
            )}
          </div>
          <div className="text-right text-sm text-white/80 flex flex-col items-end gap-1">
            <BrandingDisplay
              logoUrl={builder.logoUrl}
              companyName={builder.companyName || builder.name || "Your Builder"}
              showCompanyName={showCompanyName}
              logoSize={builder.logoSize}
              sizeClasses={{
                small: "max-h-8 max-w-[100px]",
                medium: "max-h-12 max-w-[150px]",
                large: "max-h-16 max-w-[200px]",
              }}
              className="flex flex-col items-end gap-1"
              nameClassName="font-semibold text-white"
              imageTestId="img-public-quote-logo"
              nameTestId="text-public-quote-company"
            />
            {builder.email && <div>{builder.email}</div>}
            {builder.phone && <div>{builder.phone}</div>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Status / Action banner */}
        {responded ? (
          <Card className={`border-2 ${isAccepted ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
            <CardContent className="p-5 flex items-center gap-3">
              {isAccepted ? (
                <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
              ) : (
                <MessageSquare className="h-6 w-6 text-amber-600 shrink-0" />
              )}
              <div>
                <div className="font-semibold text-[#374349]">
                  {isAccepted ? "You accepted this quote" : "You requested changes"}
                </div>
                <div className="text-sm text-muted-foreground">
                  Recorded {new Date(share.respondedAt!).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.
                  {" "}Your builder has been notified.
                </div>
                {share.responseMessage && (
                  <div className="mt-2 text-sm bg-white/60 rounded p-2 border">{share.responseMessage}</div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-[#5aabbf] bg-white">
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-[#374349]">Ready to respond?</div>
                  <div className="text-sm text-muted-foreground">Accept this quote, or request changes — your builder will be notified instantly.</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setChangesOpen(true)} data-testid="button-request-changes">
                    <MessageSquare className="h-4 w-4 mr-2" /> Request Changes
                  </Button>
                  <Button onClick={() => setAcceptOpen(true)} className="bg-[#374349] hover:bg-[#374349]/90 text-white" data-testid="button-accept-quote">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Accept Quote
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Project Details + Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-[#374349]">Project Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {quote.projectType && (
                <div>
                  <div className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Project Type</div>
                  <div className="font-medium">{quote.projectType}</div>
                </div>
              )}
              {quote.description && (
                <div>
                  <div className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Description</div>
                  <div className="whitespace-pre-wrap">{quote.description}</div>
                </div>
              )}
              {quote.validUntil && (
                <div>
                  <div className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Valid Until</div>
                  <div>{new Date(quote.validUntil).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-[#374349]">Quote Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(quote.subtotal)}</span></div>
              {(quote as any).showMarkupBreakdown !== false && (<>
                {Number(quote.contingencyAmount) > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Contingency</span><span>{fmtMoney(quote.contingencyAmount)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Overhead</span><span>{fmtMoney(quote.overheadAmount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Profit</span><span>{fmtMoney(quote.profitAmount)}</span></div>
              </>)}
              <div className="flex justify-between"><span className="text-muted-foreground">Net Total</span><span>{fmtMoney(quote.totalPrice)}</span></div>
              {quote.includeVat && (
                <div className="flex justify-between"><span className="text-muted-foreground">VAT ({quote.vatRate}%)</span><span>{fmtMoney(quote.vatAmount)}</span></div>
              )}
              <div className="flex justify-between pt-2 border-t font-bold text-base">
                <span className="text-[#374349]">Grand Total</span>
                <span className="text-[#374349]">{fmtMoney(quote.grandTotal)}</span>
              </div>
              <Button variant="outline" className="w-full mt-3" onClick={handleDownload} data-testid="button-download-public-pdf">
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Scope of Works */}
        {(() => {
          const totalPriceOnly = quote.showLineItems === false;
          return (
          <Card>
            <CardHeader>
              <CardTitle className="text-[#374349]">Scope of Works</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Summary mode — one row per category with sub-groups */}
              {categorySummaryRows ? (
                <div className="space-y-2">
                  {categorySummaryRows.map(({ category, total, subGroups }, idx) => (
                    <div key={idx} className="border rounded-lg overflow-hidden">
                      <div className="flex justify-between items-center px-4 py-3 bg-white">
                        <span className="font-medium text-[#374349]">{quoteHeadingLabel(quote.headingNames, category)}</span>
                        <span className="font-bold text-[#374349]">{fmtMoney(total)}</span>
                      </div>
                      {subGroups.length > 0 && (
                        <div className="border-t divide-y">
                          {subGroups.map(({ label, total: sgTotal, sgId }, sgIdx) => {
                            const SgIcon = sgId ? SUB_GROUP_ICONS[sgId] : undefined;
                            return (
                              <div key={sgIdx} className="flex justify-between items-center px-4 py-2.5 bg-gray-50/50">
                                <Badge variant="secondary" className="font-normal ml-2 bg-[#E8F2F4] text-[#374349]">
                                  {SgIcon && <SgIcon className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-[#65A2AE]" />}
                                  {quoteHeadingLabel(quote.headingNames, label, "subgroup")}
                                </Badge>
                                <span className="font-medium text-sm text-[#374349]">{fmtMoney(sgTotal)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                  {optionalItems.length > 0 && (
                    <div className="mt-4 border border-amber-300/40 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 py-3 px-4 bg-amber-50/60">
                        <span className="font-semibold text-sm text-amber-800">Optional Works</span>
                        <span className="text-xs text-amber-600 ml-2">— not included in the quoted price</span>
                      </div>
                      <div className="space-y-2 p-4 border-t border-amber-200/40">
                        {optionalItems.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center border border-amber-200/60 rounded-lg px-4 py-3 bg-amber-50/20">
                            <span className="font-medium text-[#374349]">{item.name}</span>
                            {!totalPriceOnly && <span className="font-bold text-[#374349]">{fmtMoney(item.subtotal)}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
              <div className="space-y-3">
                {coreItems.map((item, idx) => (
                  <div key={idx} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#374349]">{item.name}</div>
                        {quote.showItemDescriptions !== false && (item.customerDescription || item.description) && (
                          <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{item.customerDescription || item.description}</div>
                        )}
                        <Badge variant="outline" className="mt-2 text-xs whitespace-normal">{quoteCategoryLabel(quote.headingNames, item, item.category)}</Badge>
                      </div>
                      {!totalPriceOnly && (
                        <div className="text-right shrink-0">
                          {quote.showQuantityUnits !== false && (
                            <div className="text-xs text-muted-foreground">{item.quantity} {item.unit} @ {fmtMoney(item.unitCost)}</div>
                          )}
                          <div className="font-bold text-[#374349]">{fmtMoney(item.subtotal)}</div>
                        </div>
                      )}
                    </div>
                    {!totalPriceOnly && quote.showCostBreakdown && (item.labourCost || item.materialCost || item.plantCost) && (
                      <div className="mt-2 pt-2 border-t flex justify-end pr-2">
                        <div className="flex items-center gap-x-3 text-xs text-muted-foreground whitespace-nowrap max-w-full overflow-x-auto">
                          {item.labourCost && (
                            <span className="inline-flex items-center gap-1.5"><HardHat className="h-3.5 w-3.5 text-[#5aabbf]" />Labour <span className="font-medium text-[#374349]/80">{fmtMoney(item.labourCost)}</span></span>
                          )}
                          {item.labourCost && item.materialCost && <span className="text-muted-foreground/60">·</span>}
                          {item.materialCost && (
                            <span className="inline-flex items-center gap-1.5"><BrickWall className="h-3.5 w-3.5 text-[#5aabbf]" />Material <span className="font-medium text-[#374349]/80">{fmtMoney(item.materialCost)}</span></span>
                          )}
                          {(item.labourCost || item.materialCost) && item.plantCost && <span className="text-muted-foreground/60">·</span>}
                          {item.plantCost && (
                            <span className="inline-flex items-center gap-1.5"><Construction className="h-3.5 w-3.5 text-[#5aabbf]" />Plant <span className="font-medium text-[#374349]/80">{fmtMoney(item.plantCost)}</span></span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Optional Works section — items not included in the quoted price */}
                {optionalItems.length > 0 && (
                  <div className="mt-4 border border-amber-300/40 rounded-lg overflow-hidden">
                    <div className="flex items-center gap-2 py-3 px-4 bg-amber-50/60">
                      <div className="flex-1">
                        <span className="font-semibold text-sm text-amber-800">Optional Works</span>
                        <span className="text-xs text-amber-600 ml-2">— these items are not included in the quoted price</span>
                      </div>
                    </div>
                    <div className="space-y-3 p-4 border-t border-amber-200/40">
                      {optionalItems.map((item, idx) => (
                        <div key={idx} className="border border-amber-200/60 rounded-lg p-4 bg-amber-50/20">
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[#374349]">{item.name}</div>
                              {quote.showItemDescriptions !== false && (item.customerDescription || item.description) && (
                                <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{item.customerDescription || item.description}</div>
                              )}
                              <Badge variant="outline" className="mt-2 text-xs border-amber-300 text-amber-700 whitespace-normal">{quoteCategoryLabel(quote.headingNames, item, item.category)}</Badge>
                            </div>
                            {!totalPriceOnly && (
                              <div className="text-right shrink-0">
                                {quote.showQuantityUnits !== false && (
                                  <div className="text-xs text-muted-foreground">{item.quantity} {item.unit} @ {fmtMoney(item.unitCost)}</div>
                                )}
                                <div className="font-bold text-[#374349]">{fmtMoney(item.subtotal)}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              )}
            </CardContent>
          </Card>
          );
        })()}

        {(quote.paymentTerms || quote.termsConditions) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[#374349]">Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {quote.paymentTerms && (
                <div>
                  <div className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Payment Terms</div>
                  <div className="mt-1 whitespace-pre-wrap">{quote.paymentTerms}</div>
                </div>
              )}
              {quote.termsConditions && (
                <div>
                  <div className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Terms &amp; Conditions</div>
                  <div className="mt-1 whitespace-pre-wrap">{quote.termsConditions}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Builder contact */}
        <Card className="bg-[#374349] text-white">
          <CardContent className="p-6">
            <div className="text-[#5aabbf] text-xs font-semibold tracking-wider uppercase mb-2">Get In Touch</div>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
             <div className="font-semibold text-lg">
               {(showCompanyName ? builder.companyName : null) || builder.name || "Your Builder"}
             </div>
                 {showCompanyName && builder.name && builder.companyName && <div className="text-white/70 text-sm">{builder.name}</div>}
              </div>
              <div className="text-sm space-y-1">
                {builder.email && (
                  <a href={`mailto:${builder.email}`} className="flex items-center gap-2 hover:text-[#5aabbf]">
                    <Mail className="h-4 w-4" /> {builder.email}
                  </a>
                )}
                {builder.phone && (
                  <a href={`tel:${builder.phone}`} className="flex items-center gap-2 hover:text-[#5aabbf]">
                    <Phone className="h-4 w-4" /> {builder.phone}
                  </a>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-muted-foreground py-4 flex items-center justify-center gap-1.5">
          <Building2 className="h-3.5 w-3.5" /> Powered by The Proper Tool
        </div>
      </main>

      {/* Accept Dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Accept this quote?</DialogTitle>
            <DialogDescription>
              Your builder will be notified that you've accepted. You can also leave them a message.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              placeholder="Optional message (e.g. preferred start date, anything to confirm)..."
              value={responseMessage}
              onChange={e => setResponseMessage(e.target.value)}
              rows={4}
              data-testid="textarea-accept-message"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptOpen(false)}>Cancel</Button>
            <Button
              onClick={() => respondMutation.mutate("accept")}
              disabled={respondMutation.isPending}
              className="bg-[#374349] hover:bg-[#374349]/90 text-white"
              data-testid="button-confirm-accept"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {respondMutation.isPending ? "Sending..." : "Confirm Acceptance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Changes Dialog */}
      <Dialog open={changesOpen} onOpenChange={setChangesOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request changes</DialogTitle>
            <DialogDescription>
              Let your builder know what you'd like adjusted. They'll get an instant notification.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Textarea
              placeholder="What would you like to change? (e.g. swap material, adjust scope, query a price)..."
              value={responseMessage}
              onChange={e => setResponseMessage(e.target.value)}
              rows={5}
              data-testid="textarea-changes-message"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangesOpen(false)}>Cancel</Button>
            <Button
              onClick={() => respondMutation.mutate("request-changes")}
              disabled={respondMutation.isPending || !responseMessage.trim()}
              data-testid="button-confirm-changes"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {respondMutation.isPending ? "Sending..." : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
